import React, {useState, memo} from 'react';
import {Modal, Pressable, View, Image, Platform} from 'react-native';
import styled from 'styled-components/native';
import TextGeneric from '../textGeneric';
import {openUrl} from '../../utils/helpers';
import type {LinkTooltipProps} from '../../interfaces/components/linkTooltip';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';

const Row = styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

// Inline link (works for both web and mobile)
const InlineLink = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.primary + '40'};
  padding-bottom: 1px;
`;

// Mobile pill style (fallback for touch devices)
const Pill = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  padding-vertical: 4px;
  padding-horizontal: 8px;
  border-radius: 16px;
  background-color: ${({theme}) => (theme.primary || '#4a90e2') + '14'};
  border-width: 1px;
  border-style: solid;
  border-color: ${({theme}) => (theme.primary || '#4a90e2') + '22'};
`;

const TooltipCard = styled.View`
  background-color: ${({theme}) => theme.cardBackground};
  border-radius: 8px;
  padding: ${({theme}) => theme.text.s10}px;
  max-width: 90%;
  border-width: 0.5px;
  border-style: solid;
  border-color: rgba(64, 62, 63, 0.5);
`;

const Title = styled(TextGeneric)`
  font-weight: 600;
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
`;

const Description = styled(TextGeneric)`
  margin-top: 4px;
  color: ${({theme}) => theme.text.grey};
  font-size: ${({theme}) => theme.text.s8}px;
`;

const LinkText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s7}px;
`;

const External = styled(TextGeneric)`
  color: ${({theme}) => theme.primary};
  font-size: ${({theme}) => theme.text.s8}px;
  margin-left: 2px;
`;

const SmallFavicon = styled(Image)`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  border-radius: 2px;
`;

const Domain = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.light};
  opacity: 0.5;
  margin-bottom: 4px;
`;

const LinkTooltip: React.FC<LinkTooltipProps> = ({
  href,
  title,
  description,
  favicon,
  websiteName,
  genioHref,
  showName,
  children,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Get layout direction from Redux
  const layoutDirection = useSelector(
    (state: RootState) => state.chat?.selectedChatLayoutDirection || 'ltr',
  );

  const isWeb = Platform.OS === 'web';

  const handleMouseEnter = () => {
    if (!isWeb) return;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!isWeb) return;
    setIsHovered(false);
  };

  const open = () => openUrl(href);
  const openGenio = () => genioHref && openUrl(genioHref);

  const hasWebsiteName = !!(showName && websiteName);
  const isMedicalVariant = hasWebsiteName;

  // Extract domain from URL
  const extractDomain = (url: string): string => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  // Medical source variant: "Based on [Website] (Genio Summary)"
  if (isMedicalVariant) {
    if (isWeb) {
      return (
        <Row>
          <TextGeneric style={{fontWeight: '500', marginRight: 4}}>
            {layoutDirection === 'rtl' ? 'بناءً على' : 'Based on'}
          </TextGeneric>
          <InlineLink
            onPress={open}
            // @ts-ignore - web-specific props
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              borderBottomColor: isHovered ? '#4a90e260' : '#4a90e240',
              // @ts-ignore - web-specific cursor
              cursor: 'pointer',
            }}>
            {favicon && <SmallFavicon source={{uri: favicon}} />}
            <LinkText>{websiteName}</LinkText>
          </InlineLink>
          {genioHref && (
            <>
              <TextGeneric style={{marginHorizontal: 4}}>(</TextGeneric>
              <InlineLink
                onPress={openGenio}
                // @ts-ignore - web-specific cursor
                style={{cursor: 'pointer'}}>
                <SmallFavicon
                  source={{uri: `https://www.google.com/s2/favicons?sz=32&domain=geniomdai.com`}}
                />
                <LinkText>Genio Summary</LinkText>
              </InlineLink>
              <TextGeneric style={{marginLeft: 4}}>)</TextGeneric>
            </>
          )}
        </Row>
      );
    }

    // Mobile fallback with pills
    return (
      <>
        <Row>
          <TextGeneric style={{opacity: 0.8, marginRight: 4}}>Based on </TextGeneric>
          <Pill onPress={open} onLongPress={() => setModalVisible(true)}>
            {favicon && <SmallFavicon source={{uri: favicon}} />}
            <LinkText>{websiteName}</LinkText>
            <External>↗</External>
          </Pill>
          {genioHref && (
            <>
              <TextGeneric style={{marginHorizontal: 4}}>(</TextGeneric>
              <Pill onPress={openGenio}>
                <LinkText>Genio Summary</LinkText>
                <External>↗</External>
              </Pill>
              <TextGeneric>)</TextGeneric>
            </>
          )}
        </Row>

        <Modal visible={modalVisible} transparent onRequestClose={() => setModalVisible(false)}>
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#00000066',
              padding: 16,
            }}>
            <Pressable style={{width: '100%'}} onPress={() => setModalVisible(false)}>
              <TooltipCard>
                <Row style={{marginBottom: 4}}>
                  {favicon && <SmallFavicon source={{uri: favicon}} />}
                  <Domain>{extractDomain(href)}</Domain>
                </Row>
                <Title numberOfLines={2}>{title || websiteName || href}</Title>
                {!!description && <Description numberOfLines={4}>{description}</Description>}
              </TooltipCard>
            </Pressable>
          </View>
        </Modal>
      </>
    );
  }

  // Regular link (not medical source)
  if (isWeb) {
    return (
      <InlineLink
        onPress={open}
        // @ts-ignore - web-specific props
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          borderBottomColor: isHovered ? '#4a90e260' : '#4a90e240',
          // @ts-ignore - web-specific cursor
          cursor: 'pointer',
        }}>
        {favicon && <SmallFavicon source={{uri: favicon}} />}
        <LinkText>{children || title || href}</LinkText>
      </InlineLink>
    );
  }

  // Mobile fallback
  return (
    <>
      <Pill onPress={open} onLongPress={() => setModalVisible(true)}>
        {favicon && <SmallFavicon source={{uri: favicon}} />}
        <LinkText>{children || title || href}</LinkText>
        <External>↗</External>
      </Pill>

      <Modal visible={modalVisible} transparent onRequestClose={() => setModalVisible(false)}>
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#00000066',
            padding: 16,
          }}>
          <Pressable style={{width: '100%'}} onPress={() => setModalVisible(false)}>
            <TooltipCard>
              <Row style={{marginBottom: 4}}>
                {favicon && <SmallFavicon source={{uri: favicon}} />}
                <Domain>{extractDomain(href)}</Domain>
              </Row>
              <Title numberOfLines={2}>{title || href}</Title>
              {!!description && <Description numberOfLines={4}>{description}</Description>}
            </TooltipCard>
          </Pressable>
        </View>
      </Modal>
    </>
  );
};

export default memo(LinkTooltip);
